/**
 * Camera tour — plays scene.json `camera.poses[]` as a cinematic flythrough.
 *
 * A play button (bottom-center) tweens the camera through the authored poses
 * with Catmull-Rom interpolation on position + look-target (cubic, per
 * scene-schema) and linear FOV. While playing, the active nav controller is
 * paused via onPlayStart/onPlayEnd callbacks so it doesn't fight the tween;
 * on finish the controller is re-synced to the final pose and resumes.
 *
 * Stand demo: with a recorded video of the tour (setVideo, recorded by
 * demo/flythrough-video.ts), ▶ plays the video instead of flying live —
 * the screen's frame rate at full detail whatever the scene costs to draw.
 * The engine stops drawing under it; at the end the live view settles on
 * the last pose and the video fades out onto it. Pausing hands back at the
 * pose the video was showing.
 *
 * Schema (documentation/scene-schema.md):
 *   camera.poses[] = { frame, position:[x,y,z], target:[x,y,z], fov? }
 * When poses is present + non-empty the viewer shows the play button.
 */

import * as pc from 'playcanvas';

import type { CameraPose } from '../scene/scene-types';

export interface CameraTourOptions {
    parent: HTMLElement;
    app: pc.AppBase;
    cameraEntity: pc.Entity;
    poses: CameraPose[];
    /** Frames-per-second the poses were authored at (timeline rate). Default 30. */
    fps?: number;
    /** Called when playback starts — pause the nav controller here. */
    onPlayStart?: () => void;
    /** Called when playback ends/stops — resume + re-sync the controller here.
     *  Receives the final camera position + look target so the caller can
     *  re-seed an orbit controller. */
    onPlayEnd?: (finalPos: pc.Vec3, finalTarget: pc.Vec3) => void;
}

/** A recorded flythrough to play instead of the live one (stand demo). */
export interface TourVideo {
    /** Playable URL (an object URL from the offline store). */
    url: string;
    /** The video goes right after this element — the canvas, so it sits
     *  over the scene and under the chrome. */
    after: HTMLElement;
    /** Resolve once the live view has settled at full detail. */
    settle: () => Promise<unknown>;
    /** Put the live render on the rung the video was recorded at, and back. */
    hold?: () => void;
    release?: () => void;
}

const FADE_MS = 400;

const _p = new pc.Vec3();
const _t = new pc.Vec3();

// Inline SVG icons. The old unicode ▶ / ⏸ got EMOJI presentation on
// iOS/Android (colour glyphs), so the icons must be real vectors.
const PLAY_ICON =
    '<svg class="play" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M8.2 5.6v12.8a1 1 0 0 0 1.52.86l10.3-6.4a1 1 0 0 0 0-1.72L9.72 4.74a1 1 0 0 0-1.52.86z"/></svg>';
const PAUSE_ICON =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<rect x="6.5" y="5" width="4" height="14" rx="1.3"/>' +
    '<rect x="13.5" y="5" width="4" height="14" rx="1.3"/></svg>';

/** Catmull-Rom interpolation of four points at parameter u in [0,1]. */
function catmull(out: pc.Vec3, p0: pc.Vec3, p1: pc.Vec3, p2: pc.Vec3, p3: pc.Vec3, u: number): pc.Vec3 {
    const u2 = u * u;
    const u3 = u2 * u;
    out.x = 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * u + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * u2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * u3);
    out.y = 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * u + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * u2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * u3);
    out.z = 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * u + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * u2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * u3);
    return out;
}

export class CameraTour {
    private app: pc.AppBase;
    private cameraEntity: pc.Entity;
    private opts: CameraTourOptions;

    private positions: pc.Vec3[];
    private targets: pc.Vec3[];
    private fovs: number[];
    private segTimes: number[];   // cumulative seconds at each pose
    private totalTime: number;

    private button: HTMLButtonElement;

    /** The ▶ — the stand demo's tutorial points its last bubble at it. */
    get playButton(): HTMLButtonElement {
        return this.button;
    }

    private playing = false;
    private elapsed = 0;
    private video: TourVideo | null = null;
    private videoEl: HTMLVideoElement | null = null;
    private handingBack = false;
    private autoRenderBefore = true;
    private onUpdate: (dt: number) => void;

    constructor(opts: CameraTourOptions) {
        this.app = opts.app;
        this.cameraEntity = opts.cameraEntity;
        this.opts = opts;
        const fps = opts.fps ?? 30;

        // Sort poses by frame, decompose into control arrays.
        const poses = [...opts.poses].sort((a, b) => a.frame - b.frame);
        this.positions = poses.map(p => new pc.Vec3(p.position[0], p.position[1], p.position[2]));
        this.targets = poses.map(p => new pc.Vec3(p.target[0], p.target[1], p.target[2]));
        this.fovs = poses.map(p => p.fov ?? (this.cameraEntity.camera?.fov ?? 60));
        this.segTimes = [0];
        for (let i = 1; i < poses.length; i++) {
            const dtSec = Math.max(0.001, (poses[i].frame - poses[i - 1].frame) / fps);
            this.segTimes.push(this.segTimes[i - 1] + dtSec);
        }
        this.totalTime = this.segTimes[this.segTimes.length - 1] || 0;

        // Play button — styled by .ollo-tour-play in viewer.css (same glass
        // language as the toolbar), not inline styles.
        this.button = document.createElement('button');
        this.button.className = 'ollo-tour-play';
        this.button.type = 'button';
        this.button.setAttribute('aria-label', 'Play camera tour');
        this.button.innerHTML = PLAY_ICON;
        this.button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });
        opts.parent.appendChild(this.button);

        this.onUpdate = (dt: number) => this.tick(dt);
    }

    private toggle(): void {
        if (this.playing) this.stop();
        else this.play();
    }

    /** Play this recording on ▶ from now on (null = fly live again). */
    setVideo(video: TourVideo | null): void {
        this.video = video;
    }

    get hasVideo(): boolean {
        return this.video !== null;
    }

    play(): void {
        if (this.playing || this.positions.length < 2) {
            // Single pose: just snap to it.
            if (this.positions.length === 1) {
                this.cameraEntity.setPosition(this.positions[0]);
                this.cameraEntity.lookAt(this.targets[0]);
            }
            return;
        }
        this.playing = true;
        this.elapsed = 0;
        this.button.innerHTML = PAUSE_ICON;
        this.button.setAttribute('aria-label', 'Pause camera tour');
        this.button.classList.add('playing');
        this.opts.onPlayStart?.();
        console.info(this.video ? '[tour] playing the recorded video' : '[tour] no recorded video here: flying live');
        if (this.video) this.playVideo(this.video);
        else this.app.on('update', this.onUpdate);
    }

    private playVideo(v: TourVideo): void {
        const el = document.createElement('video');
        el.className = 'ollo-tour-video';
        el.muted = true;
        el.playsInline = true;
        el.setAttribute('muted', '');
        el.setAttribute('playsinline', '');
        el.src = v.url;
        v.after.after(el);
        this.videoEl = el;
        v.hold?.();
        this.autoRenderBefore = this.app.autoRender;

        // The engine stops drawing for the whole video, so the video has the
        // GPU to itself. Settling behind it on the top rung looked free but
        // is the heaviest drawing the scene does, and a view that never
        // quite settles kept it going under the whole video. The hand-back
        // settles the live view instead, on the video's last frame.
        this.applyAt(this.totalTime);
        this.app.autoRender = false;

        el.addEventListener('playing', () => {
            el.classList.add('on');
            console.info(`[tour] video ${el.videoWidth}×${el.videoHeight} shown in ${el.clientWidth}×${el.clientHeight} px`);
        }, { once: true });
        el.addEventListener('error', () => console.warn('[tour] video error', el.error?.code, el.error?.message));
        el.addEventListener('ended', () => {
            const q = el.getVideoPlaybackQuality?.();
            if (q) console.info(`[tour] video ended: ${q.totalVideoFrames} frames shown, ${q.droppedVideoFrames} dropped`);
            void this.handBack(this.totalTime);
        }, { once: true });
        el.play().catch((err) => {
            // Refused (no decoder, a broken file): fly live instead.
            console.warn('[tour] video refused, flying live', err);
            this.dropVideo();
            v.release?.();
            this.app.autoRender = this.autoRenderBefore;
            this.app.on('update', this.onUpdate);
        });
    }

    /**
     * Video → live scene at `t`: put the camera there and hand navigation
     * back BEFORE the fade — an orbit scene re-aims at its pivot on resume,
     * and that turn belongs under the video, not as a jump after it. Then
     * let the view settle and fade the video off it.
     */
    private async handBack(t: number): Promise<void> {
        const el = this.videoEl;
        const v = this.video;
        if (!el || !v || this.handingBack) return;
        this.handingBack = true;
        el.pause();
        this.app.autoRender = this.autoRenderBefore;
        this.applyAt(t);
        this.resumeNav();
        this.app.renderNextFrame = true;
        await Promise.race([v.settle(), new Promise(res => setTimeout(res, 2500))]);
        if (this.videoEl !== el) return;   // disposed meanwhile
        el.classList.remove('on');
        await new Promise(res => setTimeout(res, FADE_MS));
        if (this.videoEl !== el) return;
        this.dropVideo();
        v.release?.();
        this.resetButton();
    }

    private dropVideo(): void {
        this.videoEl?.remove();
        this.videoEl = null;
        this.handingBack = false;
    }

    stop(): void {
        if (!this.playing) return;
        if (this.videoEl) {
            void this.handBack(Math.min(this.totalTime, this.videoEl.currentTime));
            return;
        }
        this.app.off('update', this.onUpdate);
        this.resumeNav();
        this.resetButton();
    }

    /** Re-sync the controller to wherever the camera ended up. */
    private resumeNav(): void {
        const pos = this.cameraEntity.getPosition().clone();
        const fwd = this.cameraEntity.forward.clone().mulScalar(5).add(pos);
        this.opts.onPlayEnd?.(pos, fwd);
    }

    private resetButton(): void {
        this.playing = false;
        this.button.innerHTML = PLAY_ICON;
        this.button.setAttribute('aria-label', 'Play camera tour');
        this.button.classList.remove('playing');
    }

    /** Tour length in seconds. */
    get duration(): number {
        return this.totalTime;
    }

    /**
     * Put the camera where the tour is at `tSec` (clamped to the tour). The
     * live flythrough, the video recorder and the video's hand-back all go
     * through here, so a recorded frame and the live pose at the same t
     * are the same view.
     */
    applyAt(tSec: number): void {
        const last = this.positions.length - 1;
        if (last < 0) return;
        if (tSec >= this.totalTime || last === 0) {
            this.cameraEntity.setPosition(this.positions[last]);
            this.cameraEntity.lookAt(this.targets[last]);
            if (this.cameraEntity.camera) this.cameraEntity.camera.fov = this.fovs[last];
            return;
        }
        const t = Math.max(0, tSec);

        // Find the segment [i, i+1] containing t.
        let i = 0;
        while (i < this.segTimes.length - 1 && this.segTimes[i + 1] <= t) i++;
        const segStart = this.segTimes[i];
        const segEnd = this.segTimes[i + 1];
        let u = (t - segStart) / Math.max(1e-4, segEnd - segStart);
        // Ease in/out for a less mechanical feel.
        u = u * u * (3 - 2 * u);

        const n = this.positions.length;
        const i0 = Math.max(0, i - 1);
        const i2 = Math.min(n - 1, i + 1);
        const i3 = Math.min(n - 1, i + 2);

        catmull(_p, this.positions[i0], this.positions[i], this.positions[i2], this.positions[i3], u);
        catmull(_t, this.targets[i0], this.targets[i], this.targets[i2], this.targets[i3], u);
        this.cameraEntity.setPosition(_p);
        this.cameraEntity.lookAt(_t);
        if (this.cameraEntity.camera) {
            this.cameraEntity.camera.fov = this.fovs[i] + (this.fovs[i2] - this.fovs[i]) * u;
        }
    }

    private tick(dt: number): void {
        this.elapsed += dt;
        if (this.elapsed >= this.totalTime) {
            // Land exactly on the last pose, then end.
            this.applyAt(this.totalTime);
            this.stop();
            return;
        }
        this.applyAt(this.elapsed);
    }

    dispose(): void {
        if (this.playing) this.app.off('update', this.onUpdate);
        if (this.videoEl) {
            this.dropVideo();
            this.app.autoRender = this.autoRenderBefore;
        }
        this.button.remove();
    }
}
